import { useState, type MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Icon } from "@/components/Icon";
/*
  formatMoney, NOT formatMoneyWhole.

  A P&L is a statement whose lines are terms of a sum, and whole-dollar rendering makes
  the column stop adding up: on 2026-09-02 the running app showed ten expense categories
  totalling $17,989 under a stated $17,988, because "Veterinary, Breeding and Medicine" is
  $402.50 — which Intl.NumberFormat rounds half-up to 403 — while the exact $17,988.19
  rounds down. The engine and the API were both right; the screen simply disagreed with
  ITSELF, which no test caught because every test compared the screen to the API and the
  two agreed.

  formatMoneyWhole's own docstring scopes it to KPI tiles, and that stays right for them:
  a tile is a single figure with nothing to reconcile to. The dashboard tiles and the
  roll-up concept keep it. This card does not, because it is the one screen a reader adds
  up — and at a trade show it is read by people who do that for a living.

  Showing the line exactly is also the only remedy that invents nothing. Allocating the
  rounding difference onto one row would print $402 for a $402.50 line, which is the kind
  of plausible-looking filler the PaidLeave disclosure block exists to refuse.
*/
import { formatMoney } from "@/lib/format";
import { useBalanceSheet } from "@/hooks/useBalanceSheet";
import { useProfitAndLoss } from "@/hooks/useProfitAndLoss";
import type { ProfitAndLossReport } from "@/api/types";
import "./ReportsStatements.css";

type Basis = "cash" | "accrual";

const BASES: { key: Basis; label: string }[] = [
  { key: "cash", label: "Cash" },
  { key: "accrual", label: "Accrual" },
];

/**
 * Builds the CSV FINPACK export actually downloads, from the REAL report the API
 * returned — see the `onExport` note below.
 *
 * IT SAID "Never called for basis === accrual; there is no accrual report to export" until
 * 2026-09-07, and that stopped being true at ef-003. Corrected after clicking Accrual and
 * downloading `finpack-preview-accrual.csv` — a file this sentence said could not exist.
 */
function buildCsv(report: ProfitAndLossReport): string {
  const rows = [
    ["EasyFarm — P&L export preview", ""],
    ["Basis", report.basis],
    // A real timestamp of when this file was produced -- not a claimed reporting
    // period. The query behind this report sums every entry ever posted, not a
    // year-to-date window, so this export does not claim a period it cannot back up.
    ["Generated", new Date().toISOString()],
    ["", ""],
    ["Income", formatMoney(report.incomeTotal)],
    ...report.income.map((l) => [`  ${l.name}`, formatMoney(l.amount)]),
    ["Expenses", formatMoney(report.expenseTotal)],
    ...report.expenses.map((l) => [`  ${l.name}`, formatMoney(l.amount)]),
    ["Net farm income", formatMoney(report.netIncome)],
  ];
  return rows.map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\n");
}

type ExportState = "idle" | "done";

/**
 * Reports screen — the Profit &amp; Loss card is real ledger aggregation
 * (GET /api/demo/reports/profit-loss, EasyFarm.Data.DemoRepository.GetProfitAndLossLinesAsync),
 * not the reports-fixtures.ts constant this screen used to render unconditionally. Every
 * income and expense line, and every total, is a sum of that tenant's own posted entries.
 *
 * BOTH BASES ARE REAL NOW, AND THIS COMMENT SAID THE OPPOSITE FOR LONGER THAN IT SHOULD
 * HAVE. Until 2026-09-07 it read "⚠️ CASH BASIS ONLY, ON PURPOSE ... selecting Accrual
 * below renders an honest explanation instead of figures", and carried a breadcrumb saying
 * any number under Accrual "is being invented, not read from the ledger."
 *
 * That was true when it was written and ef-003 ended it: `document` and
 * `document_settlement` now carry invoices, bills and their settlements, and
 * `RecognitionRules.RecognisedAmount` turns the three per-account figures from
 * `GetAccountActivityAsync` into a cash or an accrual number. `handleExport` below already
 * said so — "the accrual basis is a real report now" — while the doc comment above it and
 * the breadcrumb still said it could not be.
 *
 * ⚠️ WHY A STALE BREADCRUMB WAS THE BUG, not just untidy prose. `rules/ui-breadcrumbs.md`
 * §5: "a breadcrumb that lies is worse than none ... treat a wrong `data-state`/`@ui-check`
 * as a bug." This one did more than mislead. It instructed any tester — a person or an
 * exploration agent — that every accrual figure on screen was fabricated, so following it
 * leads either to filing a CRITICAL against a working, deliberately built feature, or to
 * "fixing" the product by deleting it. Found on 2026-09-07 by clicking Accrual and reading
 * the code afterwards, in that order.
 *
 * THE ORIGINAL WARNING STILL STANDS where it applies — a toggle that invents a basis is
 * worse than one that says it can't, and a farmer relying on a fabricated accrual P&L at
 * tax time is a real harm. It is honoured now by DERIVING the second basis rather than by
 * refusing to show one. What must never happen is an accrual figure that is really a cash
 * figure wearing an accrual label, which is the same harm arriving by a quieter route.
 *
 * @ui-check: under Accrual, the figures rendered and the figures exported must both come
 * from the accrual report — never the cash one relabelled. The Net worth card below is
 * still the honest-explanation case: this schema records no posted asset/liability/equity
 * balance, so there is no balance sheet to sum.
 *
 * The Net worth card is the case the old paragraph described, and it has not changed: this
 * schema records single cash entries and documents, never a posted asset/liability/equity
 * balance, so there is no balance sheet to sum. It renders an honest explanation rather
 * than the old fixture's $1.84M net worth.
 *
 * The FINPACK export remains a real interaction: it downloads an actual CSV built from
 * whatever the P&L card currently shows, on EITHER basis, honestly labeled as a preview —
 * this demo has no certified FINPACK integration. The filename carries the basis so two
 * files cannot be confused in a Downloads folder, and the export is disabled while there
 * is no report behind it (still loading, or a failed request).
 *
 * RE-AUTHORED, ef-044-1: renamed from `Reports` — ADR 0009 §6 retires this screen's own
 * `AreaTabs` tab strip. It renders at `/reports/statements` now, reached from the
 * picker (`ReportsPicker.tsx`) or a bookmark, and links back with "← All reports"
 * rather than switching a tab. Everything below this point is otherwise unchanged.
 */
interface ReportsStatementsProps {
  onNavigate: (path: string) => void;
}

export function ReportsStatements({ onNavigate }: ReportsStatementsProps) {
  const [basis, setBasis] = useState<Basis>("cash");
  const [exportState, setExportState] = useState<ExportState>("idle");
  const { report, status, error } = useProfitAndLoss(basis);
  const { sheet, status: sheetStatus, error: sheetError } = useBalanceSheet();

  /* Real <a>, same modified-click passthrough AppShell.tsx's own handleNavClick uses. */
  function handleBackClick(event: MouseEvent<HTMLAnchorElement>): void {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate("/reports");
  }

  function handleExport(): void {
    // ef-003: the accrual basis is a real report now, so the export is no longer
    // cash-only. The guard that remains is the one that matters — there must BE a report
    // behind the button. The filename carries the basis, so a file cannot be mistaken for
    // the other one after it lands in Downloads.
    if (!report) return;
    // A REAL file, generated from what's on screen right now — not a fake
    // "exported!" message with nothing behind it. It's labeled a preview
    // because it isn't the certified FINPACK file format, which this demo
    // has no integration with; see the status line below the button.
    const csv = buildCsv(report);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const filename = `finpack-preview-${basis}.csv`;
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setExportState("done");
  }

  // Renamed from hasCashReport with ef-003: it is no longer cash-specific, and a name
  // that says "cash" on a value used for both bases is the kind of stale label that
  // outlives the behaviour it described.
  const hasReport = status === "ready" && report !== null;

  /**
   * THE EXPORT MUST NOT SAY WHAT THE SCREEN REFUSES TO SAY.
   *
   * MEASURED ON THE BOOTH TENANT IMMEDIATELY AFTER A RESET, 2026-09-08 — the state a
   * prospect sees at the START of every conversation, because Christy resets between them.
   * The screen said, in two places and in so many words:
   *
   *     "No categorized activity yet"
   *     "No balance sheet yet ... a zero would be an invented figure, not a measured one."
   *
   * and the button beside them was live. Clicking it downloaded finpack-preview-cash.csv,
   * 158 bytes:
   *
   *     "Income","$0.00"
   *     "Expenses","$0.00"
   *     "Net farm income","$0.00"
   *
   * Exactly the invented figure, in the one artifact that LEAVES THE BUILDING. FINPACK is
   * real software these farmers import into; a file stating a farm's net income is $0.00
   * carries more authority than a screen does, not less, because it outlives the
   * conversation that produced it.
   *
   * `hasReport` was never the right gate for this: the API answers an empty tenant with a
   * perfectly valid report whose every figure is zero, so the button stayed enabled exactly
   * when there was nothing to export. Gated now on the SAME condition the P&L's own empty
   * state uses, one line of which is above — one definition of "there is nothing here", so
   * the screen and the file it produces cannot disagree again.
   */
  const reportHasLines =
    report !== null && (report.income.length > 0 || report.expenses.length > 0);
  const exportDisabled = !hasReport || !reportHasLines;

  /*
   * <main>, not a div. AppShell's own comment already promised this — "each page
   * (Dashboard, Payroll, Reports, Capture) owns exactly one <main> of its own" — and the
   * implementation had drifted from it: Payroll, Reports and Capture rendered no landmark
   * at all, so the skip link landed on a focus container and a screen-reader user had no
   * way to jump to the primary content of three of the five owner screens.
   *
   * The axe gate did not catch it because `landmark-one-main` is MODERATE impact and the
   * gate filters to serious/critical — a deliberate, defensible choice (a gate that cries
   * wolf gets switched off) with a blind spot this fell straight into. Found by walking
   * the DOM of every route instead of trusting the gate.
   */
  return (
    <main className="reports-page">
      {/* The subtitle follows the toggle. It read "cash basis" unconditionally until
          ef-003 — true while there was only one basis, and a quiet lie the moment Accrual
          computed: the page header contradicting the card beside it. */}
      <PageHeader
        title="Reports"
        subtitle={`Profit & Loss · all-time, ${basis} basis`}
        action={
          <div className="reports-page__toggle" role="group" aria-label="Accounting basis">
            {BASES.map((b) => (
              <button
                key={b.key}
                type="button"
                aria-pressed={basis === b.key}
                className={`reports-page__toggle-btn${basis === b.key ? " reports-page__toggle-btn--on" : ""}`}
                onClick={() => setBasis(b.key)}
              >
                {b.label}
              </button>
            ))}
          </div>
        }
      />

      <a href="/reports" className="reports-page__back" onClick={handleBackClick}>
        ← All reports
      </a>

      <div className="reports-page__grid">
        <section
          className="reports-page__pl"
          aria-labelledby="pl-heading"
          data-state={status}
        >
          <div className="reports-page__card-head">
            <h2 id="pl-heading" className="reports-page__card-title">
              Profit &amp; Loss
            </h2>
            {/*
              Echoes report.basis — what the server says it COMPUTED — not the toggle
              state. If the two ever disagree, the label is the thing that tells you,
              rather than confidently mislabelling a cash report as accrual.
            */}
            <span className="reports-page__basis-label">
              {report ? `${report.basis} basis` : `${basis} basis`}
            </span>
          </div>

          {status === "loading" ? (
            <StateMessage tone="loading" title="Loading the ledger…" />
          ) : status === "error" ? (
            <StateMessage
              tone="error"
              title="Could not load the Profit & Loss report"
              detail={error ?? undefined}
            />
          ) : !report || (report.income.length === 0 && report.expenses.length === 0) ? (
            <StateMessage
              tone="empty"
              title="No categorized activity yet"
              detail={
                basis === "accrual"
                  ? "Accrual recognises invoices and bills when they are incurred. This farm has none recorded, and its entries need an account (category) before they appear here."
                  : "Entries need an account (category) before they appear on the P&L."
              }
            />
          ) : (
            <>
              <div className="reports-page__line reports-page__line--head reports-page__amount-line">
                <span>Income</span>
                <span>{formatMoney(report.incomeTotal)}</span>
              </div>
              {report.income.map((line) => (
                <div key={line.accountId} className="reports-page__line reports-page__amount-line">
                  <span>{line.name}</span>
                  <span>{formatMoney(line.amount)}</span>
                </div>
              ))}

              <div className="reports-page__line reports-page__line--head reports-page__amount-line">
                <span>Expenses</span>
                <span>{formatMoney(report.expenseTotal)}</span>
              </div>
              {report.expenses.map((line) => (
                <div key={line.accountId} className="reports-page__line reports-page__amount-line">
                  <span>{line.name}</span>
                  <span>{formatMoney(line.amount)}</span>
                </div>
              ))}

              <div className="reports-page__line reports-page__line--head reports-page__line--net reports-page__amount-line">
                <span>Net farm income</span>
                <span>{formatMoney(report.netIncome)}</span>
              </div>
            </>
          )}
        </section>

        <section
          className="reports-page__networth"
          aria-labelledby="networth-heading"
          data-state={sheetStatus === "ready" && !sheet?.hasOpeningBalances ? "empty" : sheetStatus}
        >
          <div className="reports-page__card-head">
            <h2 id="networth-heading" className="reports-page__card-title">
              Net worth
            </h2>
            {/*
              NOT "FINPACK-ready". FINPACK is real software that Minnesota farm
              financial consultants actually use, and a farm-show audience reads that
              badge literally: "this exports to FINPACK today." It does not — the export
              is a CSV of the real P&L above, not the certified file format, which the
              status line under the Export button says plainly.
            */}
            {/*
              ⚠️ THIS CAPTION AND THE BUTTON BELOW LIVE IN THE NET WORTH CARD, AND THE FILE
              THEY PRODUCE CONTAINS ONLY THE P&L. Nothing from this card is in it — no
              assets, no liabilities, no receivable or payable, not the net worth figure.

              MEASURED 2026-09-08 by downloading both:

                finpack-preview-cash.csv     "EasyFarm — P&L export preview"
                                             Income 78,730.00 / Expenses 18,885.78 /
                                             Net farm income 59,844.22
                finpack-preview-accrual.csv  Income 85,680.00 / Expenses 23,166.23 /
                                             Net farm income 62,513.77

              Both match the screen line for line, and the filename carries the basis — the
              export itself is correct and honest about being a P&L. It is the PLACEMENT
              that misleads: a farm consultant who knows FINPACK, clicking an export from a
              balance-sheet card, reasonably expects a balance sheet.

              The button's own state agrees with the file rather than with its surroundings:
              `exportDisabled = !hasReport || !reportHasLines` is computed entirely from the
              P&L, so this control's enabled/disabled behaviour already tracks the OTHER card.

              NOT MOVED OR RENAMED HERE. Renaming breaks six locators in reports.spec.ts
              (all `/export to finpack/i`, which "Export P&L to FINPACK" would not match) and
              moving it relocates the disabled-state status line with it — a structural change
              to a two-card layout seven days from freeze, for a file that is already correct.
              Queued as Q-FINPACK-EXPORT-LIVES-IN-THE-WRONG-CARD with the evidence above.
            */}
            <span className="reports-page__basis-label">FINPACK export · preview</span>
          </div>

          {/*
            @ui-check: when sheet.hasOpeningBalances is false render the EmptyState, never
            $0.00. "No balance sheet" and "a net worth of zero" are different claims, and
            only one of them is true for a farm that has not recorded openings. The Booth
            Demo tenant is that case on every deployment (seed 0013 is Live Wall only), so
            this branch is exercised rather than assumed.
          */}
          {sheetStatus === "loading" ? (
            <StateMessage tone="loading" title="Loading the balance sheet…" />
          ) : sheetStatus === "error" ? (
            <StateMessage
              tone="error"
              title="Could not load the balance sheet"
              detail={sheetError ?? undefined}
            />
          ) : !sheet || !sheet.hasOpeningBalances ? (
            <StateMessage
              tone="empty"
              title="No balance sheet yet"
              detail="Net worth needs posted asset, liability, and equity balances. This farm hasn't recorded any opening balances, so there is nothing here to sum — a zero would be an invented figure, not a measured one."
            />
          ) : (
            <>
              <div className="reports-page__line reports-page__line--head reports-page__amount-line">
                <span>Assets</span>
                <span>{formatMoney(sheet.assetTotal)}</span>
              </div>
              {sheet.assets.map((line) => (
                <div key={line.accountId} className="reports-page__line reports-page__amount-line">
                  <span>{line.name}</span>
                  <span>{formatMoney(line.balance)}</span>
                </div>
              ))}

              <div className="reports-page__line reports-page__line--head reports-page__amount-line">
                <span>Liabilities</span>
                <span>{formatMoney(sheet.liabilityTotal)}</span>
              </div>
              {sheet.liabilities.length === 0 ? (
                <div className="reports-page__line reports-page__amount-line">
                  <span>No debt recorded</span>
                  <span>{formatMoney(0)}</span>
                </div>
              ) : (
                sheet.liabilities.map((line) => (
                  <div key={line.accountId} className="reports-page__line reports-page__amount-line">
                    <span>{line.name}</span>
                    <span>{formatMoney(line.balance)}</span>
                  </div>
                ))
              )}

              {/*
                RECEIVABLES AND PAYABLES APPEAR UNDER ACCRUAL ONLY, and that is correct
                accounting rather than a limitation: a cash-basis balance sheet has no
                receivables. Under Cash the figures below are the posted ledger and nothing
                else, which is why `balances` can assert an exact identity on them.
              */}
              {basis === "accrual" && (
                <>
                  <div className="reports-page__line reports-page__line--head reports-page__amount-line">
                    <span>Receivable</span>
                    <span>{formatMoney(sheet.receivable)}</span>
                  </div>
                  <div className="reports-page__line reports-page__line--head reports-page__amount-line">
                    <span>Payable</span>
                    <span>{formatMoney(sheet.payable)}</span>
                  </div>
                </>
              )}

              <div className="reports-page__line reports-page__line--head reports-page__line--net reports-page__amount-line">
                <span>Net worth</span>
                <span>
                  {formatMoney(
                    basis === "accrual"
                      ? sheet.netWorth + sheet.receivable - sheet.payable
                      : sheet.netWorth,
                  )}
                </span>
              </div>

              {/*
                Shown ONLY when the server says the books do not add up. It is not a
                decoration: `balances` is the open-books identity checked in exact decimal
                (assets - liabilities = equity + net income), and a sheet that fails it is
                displaying numbers a farmer should not act on. Silence when true, a plain
                statement when false — never a green tick claiming a check that passed,
                which trains people to stop reading it.
              */}
              {!sheet.balances && (
                <p className="reports-page__note" role="status" data-state="error">
                  These figures don't balance. Assets less liabilities should equal equity
                  plus net farm income, and here they don't — treat the numbers above as
                  unreliable and check the ledger.
                </p>
              )}
            </>
          )}

          <button
            type="button"
            className="btn btn--ghost reports-page__export"
            onClick={handleExport}
            disabled={exportDisabled}
          >
            <Icon name="download" size={16} />
            Export to FINPACK
          </button>
          {exportDisabled && hasReport && (
            /*
              A disabled control with no reason is its own small dead end — a visitor taps
              it, nothing happens, and the screen never says why. The empty state above says
              it for a sighted reader in context; this says it for anyone tabbing to the
              button, and it is the reason rather than an apology.
            */
            <p className="reports-page__export-note" role="note">
              Nothing to export yet — the Profit &amp; Loss above has no categorized entries,
              and a file of zeros would say something this screen will not.
            </p>
          )}
          {exportState === "done" && (
            <p className="reports-page__export-note" role="status">
              Downloaded finpack-preview-{basis}.csv — a preview export built from the real
              Profit &amp; Loss above. This demo has no certified FINPACK integration
              behind it yet.
            </p>
          )}
        </section>
      </div>
    </main>
  );
}
